import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Gamepad2,
  Users,
  BarChart3,
  Store,
  Plus,
  Search,
  Copy,
  QrCode,
  Download,
  Pencil,
  Trash2,
  FileText,
  Dice5,
  Store as StoreIcon,
  AlertCircle,
  FileSpreadsheet,
  CheckCircle2,
  ShieldCheck,
  Clock,
  RefreshCcw,
  MapPin,
  Truck,
  PackageCheck,
  Trophy,
  Files,
} from "lucide-react";
import LegalFooter from "@/components/LegalFooter";
import Navbar from "@/components/Navbar";
import { Kpi } from "@/components/Kpi";
import { PlaysTimeseries, CtaBar, MerchantSparkline } from "@/components/Charts";
import PerGameTable from "@/components/PerGameTable";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import EmailDiagnosticCard from "@/components/EmailDiagnosticCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import RulesDialog from "@/components/RulesDialog";
import DuplicateGameDialog from "@/components/DuplicateGameDialog";
import QRCodeDialog from "@/components/QRCodeDialog";
import StatusPill from "@/components/StatusPill";
import LotteryDrawDialog from "@/components/LotteryDrawDialog";
import PredictionDrawDialog from "@/components/PredictionDrawDialog";
import GuessPriceDrawDialog from "@/components/GuessPriceDrawDialog";
import NinjaDrawDialog from "@/components/NinjaDrawDialog";
import MerchantLogoUploader from "@/components/MerchantLogoUploader";
import ReceiptCell from "@/components/ReceiptCell";
import PredictionsDetailDialog from "@/components/PredictionsDetailDialog";
import { formatDate } from "@/lib/format";
import { LanguageSelect } from "@/components/LanguageSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");

  const tabs = [
    { id: "overview", label: t("dashboard.overview"), Icon: LayoutDashboard },
    { id: "games", label: t("dashboard.games"), Icon: Gamepad2 },
    { id: "merchants", label: t("dashboard.merchants"), Icon: Store },
    { id: "participants", label: t("dashboard.participants"), Icon: Users },
    { id: "analytics", label: t("dashboard.analytics"), Icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-muted/40">
      <Navbar variant="app" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              {t("dashboard.role_admin")}
            </div>
            <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
              {t("dashboard.welcome")}, {user?.name?.split(" ")[0]}
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1 p-1 bg-card border border-border rounded-2xl w-full overflow-x-auto mb-6">
          {tabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === id
                  ? "bg-foreground text-background shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "games" && <GamesPanel role="admin" onNavigate={setTab} />}
        {tab === "merchants" && <MerchantsPanel />}
        {tab === "participants" && <ParticipantsPanel role="admin" />}
        {tab === "analytics" && <AnalyticsPanel />}

        <LegalFooter className="mt-10 pt-6 border-t border-border/40" />
      </div>
    </div>
  );
}

/* ---------- OVERVIEW ---------- */
function Overview() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [perGame, setPerGame] = useState([]);
  const [topMerchants, setTopMerchants] = useState([]);

  useEffect(() => {
    api
      .get("/analytics/overview")
      .then((r) => setData(r.data))
      .catch(() => {});
    api
      .get("/analytics/per-game")
      .then((r) => setPerGame(r.data.games))
      .catch(() => {});
    api
      .get("/analytics/top-merchants")
      .then((r) => setTopMerchants(r.data.merchants || []))
      .catch(() => {});
  }, []);

  if (!data)
    return <div className="text-muted-foreground">{t("dashboard.loading")}</div>;

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi
          label={t("dashboard.kpi_plays")}
          value={data.total_plays}
          testId="kpi-plays"
        />
        <Kpi
          label={t("dashboard.kpi_leads")}
          value={data.total_leads}
          testId="kpi-leads"
        />
        <Kpi
          label={t("dashboard.kpi_conversion")}
          value={`${data.conversion_rate}%`}
          testId="kpi-conversion"
        />
        <Kpi
          label={t("dashboard.kpi_games")}
          value={data.active_games ?? data.total_games}
          testId="kpi-games"
        />
        <Kpi
          label={t("dashboard.kpi_merchants")}
          value={data.total_merchants}
          testId="kpi-merchants"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 qrise-card p-6">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
                {t("dashboard.timeseries")}
              </div>
            </div>
          </div>
          <PlaysTimeseries data={data.timeseries} />
        </div>
        <div className="qrise-card p-6">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-6">
            {t("dashboard.cta_performance")}
          </div>
          {data.cta_stats.length ? (
            <CtaBar data={data.cta_stats} />
          ) : (
            <div className="text-sm text-muted-foreground">
              {t("dashboard.no_data")}
            </div>
          )}
        </div>
      </div>

      <div className="qrise-card p-6" data-testid="top-merchants-panel">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
              {t("dashboard.top_merchants_title")}
            </div>
            <div className="mt-1 text-xs text-muted-foreground/80">
              {t("dashboard.top_merchants_subtitle")}
            </div>
          </div>
        </div>
        {topMerchants.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {t("dashboard.top_merchants_empty")}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            {topMerchants.map((m, idx) => {
              const colors = ["#f59e0b", "#8b5cf6", "#3b82f6"];
              const rankBadges = [
                "bg-amber-400/20 text-amber-700 dark:text-amber-300 ring-amber-400/40",
                "bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/30",
                "bg-blue-500/15 text-blue-700 dark:text-blue-300 ring-blue-500/30",
              ];
              return (
                <div
                  key={m.merchant_id}
                  className="qrise-card-hover p-4 rounded-xl border border-border/60 bg-gradient-to-br from-background to-muted/20"
                  data-testid={`top-merchant-${m.slug}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ring-1 ${rankBadges[idx]}`}
                        >
                          #{idx + 1}
                        </span>
                        <div className="font-semibold tracking-tight truncate">
                          {m.name}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t("dashboard.plays_7d", { count: m.plays_7d })} ·{" "}
                        {t("dashboard.leads_7d", { count: m.leads_7d })}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-lg font-bold leading-none">
                        {m.conversion_rate}%
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                        {t("dashboard.conversion_short")}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 -mx-1">
                    <MerchantSparkline
                      data={m.sparkline}
                      color={colors[idx]}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>


      <div className="qrise-card p-6">
        <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">
          {t("dashboard.per_game")}
        </div>
        {perGame.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("dashboard.th_game")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_plays")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_wins")}</TableHead>
                <TableHead className="text-right">{t("dashboard.th_conversion")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perGame.map((g) => (
                <TableRow key={g.game_id}>
                  <TableCell className="font-medium">{g.title}</TableCell>
                  <TableCell className="text-right">{g.plays}</TableCell>
                  <TableCell className="text-right">{g.wins}</TableCell>
                  <TableCell className="text-right">{g.conversion}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-sm text-muted-foreground">
            {t("dashboard.no_data")}
          </div>
        )}
      </div>

      <EmailDiagnosticCard />
    </div>
  );
}

/* ---------- GAMES ---------- */
export function GamesPanel({ role = "admin", onNavigate }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [rulesGameId, setRulesGameId] = useState(null);
  const [lotteryGame, setLotteryGame] = useState(null);
  const [predictionGame, setPredictionGame] = useState(null);
  const [guessPriceGame, setGuessPriceGame] = useState(null);
  const [ninjaGame, setNinjaGame] = useState(null);
  const [duplicateGameState, setDuplicateGameState] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [gameType, setGameType] = useState("spin_wheel");
  const [gameLang, setGameLang] = useState(null);
  const [filter, setFilter] = useState("");
  const [merchantFilter, setMerchantFilter] = useState("__all__");
  const [activeFilter, setActiveFilter] = useState("__all__");

  async function load() {
    const r = await api.get("/games");
    setGames(r.data.games);
    if (role === "admin") {
      try {
        const m = await api.get("/merchants");
        setMerchants(m.data.merchants);
      } catch {}
    }
  }
  useEffect(() => {
    load();
  }, []);

  // New game defaults to the assigned advertiser's language (editable).
  const newGameLang =
    gameLang || (merchantId ? merchants.find((m) => m.id === merchantId)?.language || "fr" : "en");

  async function create() {
    if (!title.trim()) return;
    try {
      const r = await api.post("/games", {
        title,
        type: gameType,
        merchant_id: merchantId || null,
        language: newGameLang,
      });
      toast.success(t("dashboard.game_created"));
      setOpenNew(false);
      setTitle("");
      setMerchantId("");
      setGameType("spin_wheel");
      setGameLang(null);
      navigate(`/admin/games/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(id) {
    if (!window.confirm(t("dashboard.confirm_delete_game"))) return;
    await api.delete(`/games/${id}`);
    toast.success(t("dashboard.game_deleted"));
    load();
  }

  async function duplicateGame(g) {
    setDuplicateGameState(g);
  }

  async function performDuplicate(targetMerchantId) {
    if (!duplicateGameState) return;
    setDuplicateLoading(true);
    try {
      const body = targetMerchantId ? { merchant_id: targetMerchantId } : {};
      const r = await api.post(
        `/games/${duplicateGameState.id}/duplicate`,
        body,
      );
      toast.success(
        t("dashboard.game_duplicated", "Jeu dupliqué — édition de la nouvelle copie"),
      );
      setDuplicateGameState(null);
      navigate(`/admin/games/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDuplicateLoading(false);
    }
  }

  async function toggleActive(game) {
    const next = !game.active;
    setGames((prev) =>
      prev.map((g) => (g.id === game.id ? { ...g, active: next } : g))
    );
    try {
      await api.patch(`/games/${game.id}`, { active: next });
      toast.success(
        next ? t("dashboard.game_activated") : t("dashboard.game_deactivated")
      );
    } catch (e) {
      setGames((prev) =>
        prev.map((g) => (g.id === game.id ? { ...g, active: !next } : g))
      );
      toast.error(formatApiError(e));
    }
  }

  const filtered = games.filter((g) => {
    const matchesText = g.title.toLowerCase().includes(filter.toLowerCase());
    const matchesMerchant =
      merchantFilter === "__all__"
        ? true
        : merchantFilter === "__none__"
          ? !g.merchant_id
          : g.merchant_id === merchantFilter;
    const matchesActive =
      activeFilter === "__all__"
        ? true
        : activeFilter === "active"
          ? !!g.active
          : !g.active;
    return matchesText && matchesMerchant && matchesActive;
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("dashboard.search")}
            className="pl-9 h-10 rounded-xl"
            data-testid="games-search-input"
          />
        </div>
        {role === "admin" && merchants.length > 0 && (
          <Select value={merchantFilter} onValueChange={setMerchantFilter}>
            <SelectTrigger
              className="h-10 rounded-xl w-56"
              data-testid="games-merchant-filter"
            >
              <SelectValue placeholder={t("dashboard.filter_by_merchant")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">
                {t("dashboard.all_merchants")}
              </SelectItem>
              <SelectItem value="__none__">
                <span className="inline-flex items-center gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 text-rose-500" />
                  {t("dashboard.unassigned_merchant")}
                </span>
              </SelectItem>
              {merchants.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger
            className="h-10 rounded-xl w-44"
            data-testid="games-active-filter"
          >
            <SelectValue placeholder={t("dashboard.filter_by_status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {t("dashboard.all_statuses")}
            </SelectItem>
            <SelectItem value="active">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                {t("dashboard.status_active")}
              </span>
            </SelectItem>
            <SelectItem value="inactive">
              <span className="inline-flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
                {t("dashboard.status_inactive")}
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {role === "admin" && (
          <Button
            onClick={() => setOpenNew(true)}
            className="rounded-xl h-10 bg-foreground text-background hover:bg-foreground/90 ml-auto"
            data-testid="new-game-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            {t("dashboard.new_game")}
          </Button>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((g) => {
          const merchant = merchants.find((m) => m.id === g.merchant_id);
          return (
            <div
              key={g.id}
              className="qrise-card qrise-card-hover p-5 flex flex-col"
              data-testid={`game-card-${g.slug}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-semibold tracking-tight truncate">
                    {g.title}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {t("dashboard.plays_count", { count: g.plays_count })}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {merchant ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 text-[10px] font-semibold tracking-wide max-w-[180px]"
                        data-testid={`game-merchant-${g.slug}`}
                        title={merchant.name}
                      >
                        <StoreIcon className="h-3 w-3 shrink-0" />
                        <span className="truncate">{merchant.name}</span>
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ring-1 ring-rose-500/30"
                        data-testid={`game-unassigned-${g.slug}`}
                      >
                        <AlertCircle className="h-3 w-3" />
                        {t("dashboard.unassigned_merchant")}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 text-violet-700 dark:text-violet-300 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                      {g.type === "scratch_card"
                        ? t("landing.game_scratch")
                        : g.type === "card_game"
                          ? t("landing.game_card")
                          : g.type === "slot_machine"
                            ? t("landing.game_slot")
                            : g.type === "lottery_draw"
                              ? t("landing.game_lottery")
                              : g.type === "prediction_match"
                                ? t("landing.game_prediction")
                                : g.type === "guess_price"
                                  ? t("landing.game_price")
                                  : g.type === "quiz"
                                    ? t("landing.game_quiz", "Quiz")
                                  : g.type === "ninja"
                                    ? t("landing.game_ninja")
                                    : g.type === "flappy"
                                      ? t("landing.game_flappy")
                                      : g.type === "mystery_box"
                                        ? t("landing.game_mystery", "Boîte mystère")
                                        : g.type === "blow_candles"
                                          ? t("landing.game_blow_candles")
                                          : t("landing.game_spin")}
                    </span>
                    {(g.start_date || g.end_date) && (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground px-2 py-0.5 text-[10px] font-medium tracking-wide"
                        data-testid={`game-dates-${g.slug}`}
                        title={t("dashboard.dates_tooltip")}
                      >
                        {formatDate(g.start_date) || "…"}
                        <span className="opacity-60">→</span>
                        {formatDate(g.end_date) || "…"}
                      </span>
                    )}
                  </div>
                </div>
                <StatusPill
                  status={g.status}
                  asButton
                  onClick={() => toggleActive(g)}
                  title={
                    g.active
                      ? t("dashboard.toggle_deactivate_tooltip")
                      : t("dashboard.toggle_activate_tooltip")
                  }
                />
              </div>

              <div
                className="mt-4 h-28 rounded-xl qrise-gradient-soft flex items-center justify-center relative overflow-hidden"
                style={{
                  background: `linear-gradient(135deg, ${g.theme_color || "#8B5CF6"}25, #3b82f625 50%, #ec489925)`,
                }}
              >
                <div
                  className="h-20 w-20 rounded-full border-4 border-white dark:border-zinc-900"
                  style={{
                    background: `conic-gradient(${(g.segments || [])
                      .map(
                        (s, i) =>
                          `${s.color} ${(i * 360) / (g.segments.length || 1)}deg ${((i + 1) * 360) / (g.segments.length || 1)}deg`
                      )
                      .join(",")})`,
                  }}
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 rounded-lg text-xs"
                  onClick={() => setRulesGameId(g.id)}
                  data-testid={`rules-${g.slug}`}
                >
                  <FileText className="h-3 w-3 mr-1.5" />
                  {t("rules.edit_btn")}
                </Button>
                {g.type === "lottery_draw" && (
                  <Button
                    size="sm"
                    className="h-8 rounded-lg text-xs font-bold uppercase tracking-wider"
                    onClick={() => setLotteryGame(g)}
                    data-testid={`lottery-draw-${g.slug}`}
                    style={{
                      background:
                        "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                      color: "#1a1410",
                      boxShadow:
                        "0 4px 12px -2px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
                    }}
                  >
                    <Dice5 className="h-3 w-3 mr-1.5" />
                    {t("lottery.btn_draw")}
                  </Button>
                )}
                {g.type === "prediction_match" && (
                  <Button
                    size="sm"
                    className="h-8 rounded-lg text-xs font-bold uppercase tracking-wider"
                    onClick={() => setPredictionGame(g)}
                    data-testid={`prediction-draw-${g.slug}`}
                    style={{
                      background:
                        "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                      color: "#1a1410",
                      boxShadow:
                        "0 4px 12px -2px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
                    }}
                  >
                    <Dice5 className="h-3 w-3 mr-1.5" />
                    {t("lottery.btn_draw")}
                  </Button>
                )}
                {g.type === "guess_price" && (
                  <Button
                    size="sm"
                    className="h-8 rounded-lg text-xs font-bold uppercase tracking-wider"
                    onClick={() => setGuessPriceGame(g)}
                    data-testid={`guess-price-draw-${g.slug}`}
                    style={{
                      background:
                        "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                      color: "#1a1410",
                      boxShadow:
                        "0 4px 12px -2px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
                    }}
                  >
                    <Dice5 className="h-3 w-3 mr-1.5" />
                    {t("lottery.btn_draw")}
                  </Button>
                )}
                {g.type === "ninja" && (
                  <Button
                    size="sm"
                    className="h-8 rounded-lg text-xs font-bold uppercase tracking-wider"
                    onClick={() => setNinjaGame(g)}
                    data-testid={`ninja-draw-${g.slug}`}
                    style={{
                      background:
                        "linear-gradient(180deg, #f5e4bf 0%, #d4af37 55%, #a98027 100%)",
                      color: "#1a1410",
                      boxShadow:
                        "0 4px 12px -2px rgba(212,175,55,0.45), inset 0 1px 0 rgba(255,255,255,0.3)",
                    }}
                  >
                    <Dice5 className="h-3 w-3 mr-1.5" />
                    {t("lottery.btn_draw")}
                  </Button>
                )}
                <div className="flex-1" />
                {onNavigate && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-lg text-xs"
                    onClick={() => {
                      try {
                        sessionStorage.setItem("qrise_preselect_game", g.id);
                      } catch {
                        /* ignore */
                      }
                      onNavigate("participants");
                    }}
                    data-testid={`view-participants-${g.slug}`}
                    title={t("dashboard.view_participants")}
                  >
                    <Users className="h-3 w-3 mr-1.5" />
                    <span className="hidden sm:inline">
                      {t("dashboard.view_participants")}
                    </span>
                  </Button>
                )}
                {role === "admin" && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 rounded-lg text-xs"
                      onClick={() => navigate(`/admin/games/${g.id}`)}
                      data-testid={`edit-${g.slug}`}
                    >
                      <Pencil className="h-3 w-3 mr-1.5" />
                      {t("common.edit")}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 rounded-lg text-xs"
                      onClick={() => duplicateGame(g)}
                      data-testid={`duplicate-${g.slug}`}
                      title={t(
                        "dashboard.duplicate_hint",
                        "Crée une copie désactivée de ce jeu",
                      )}
                    >
                      <Files className="h-3 w-3 mr-1.5" />
                      <span className="hidden sm:inline">
                        {t("dashboard.duplicate", "Dupliquer")}
                      </span>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 rounded-lg text-xs text-destructive hover:text-destructive"
                      onClick={() => remove(g.id)}
                      data-testid={`delete-${g.slug}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
                {role === "merchant" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 rounded-lg text-xs"
                    onClick={() => navigate(`/merchant/games/${g.id}`)}
                    data-testid={`edit-${g.slug}`}
                  >
                    <Pencil className="h-3 w-3 mr-1.5" />
                    {t("common.edit")}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* New game dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent data-testid="new-game-dialog">
          <DialogHeader>
            <DialogTitle>{t("dashboard.new_game")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.title")}
              </Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1.5 rounded-xl h-10"
                data-testid="new-game-title-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.game_type")}
              </Label>
              <Select value={gameType} onValueChange={setGameType}>
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="new-game-type-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spin_wheel">
                    {t("landing.game_spin")}
                  </SelectItem>
                  <SelectItem value="scratch_card">
                    {t("landing.game_scratch")}
                  </SelectItem>
                  <SelectItem value="card_game">
                    {t("landing.game_card")}
                  </SelectItem>
                  <SelectItem value="slot_machine">
                    {t("landing.game_slot")}
                  </SelectItem>
                  <SelectItem value="lottery_draw">
                    {t("landing.game_lottery")}
                  </SelectItem>
                  <SelectItem value="prediction_match">
                    {t("landing.game_prediction")}
                  </SelectItem>
                  <SelectItem value="guess_price">
                    {t("landing.game_price")}
                  </SelectItem>
                  <SelectItem value="quiz">
                    {t("landing.game_quiz", "Quiz")}
                  </SelectItem>
                  <SelectItem value="ninja">
                    {t("landing.game_ninja")}
                  </SelectItem>
                  <SelectItem value="blow_candles">
                    {t("landing.game_blow_candles")}
                  </SelectItem>
                  <SelectItem value="flappy">
                    {t("landing.game_flappy", "Flappy Mascotte")}
                  </SelectItem>
                  <SelectItem value="mystery_box">
                    {t("landing.game_mystery", "Boîte mystère")}
                  </SelectItem>
                  <SelectItem value="registration_gift">
                    {t("landing.game_registration_gift", "Inscription cadeau")}
                  </SelectItem>
                  <SelectItem value="advent_calendar">
                    {t("landing.game_avent", "Calendrier de l'Avent")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium">
                {t("game_editor.assign")}
              </Label>
              <Select value={merchantId} onValueChange={setMerchantId}>
                <SelectTrigger
                  className="mt-1.5 rounded-xl h-10"
                  data-testid="new-game-merchant-select"
                >
                  <SelectValue placeholder={t("game_editor.none")} />
                </SelectTrigger>
                <SelectContent>
                  {merchants.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <LanguageSelect
              label={t("game_editor.new_game_language")}
              value={newGameLang}
              onChange={setGameLang}
              testId="new-game-language-select"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenNew(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={create}
              data-testid="new-game-create-btn"
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rules dialog */}
      <RulesDialog
        gameId={rulesGameId}
        open={!!rulesGameId}
        onOpenChange={(o) => !o && setRulesGameId(null)}
      />

      {/* Lottery draw dialog */}
      {lotteryGame && (
        <LotteryDrawDialog
          open={!!lotteryGame}
          onOpenChange={(o) => !o && setLotteryGame(null)}
          game={lotteryGame}
        />
      )}

      {/* Prediction draw dialog */}
      {predictionGame && (
        <PredictionDrawDialog
          open={!!predictionGame}
          onOpenChange={(o) => !o && setPredictionGame(null)}
          game={predictionGame}
        />
      )}

      {/* Guess-price draw dialog */}
      {guessPriceGame && (
        <GuessPriceDrawDialog
          open={!!guessPriceGame}
          onOpenChange={(o) => !o && setGuessPriceGame(null)}
          game={guessPriceGame}
        />
      )}

      {/* Ninja draw dialog */}
      {ninjaGame && (
        <NinjaDrawDialog
          open={!!ninjaGame}
          onOpenChange={(o) => !o && setNinjaGame(null)}
          game={ninjaGame}
        />
      )}

      {/* Duplicate game dialog */}
      <DuplicateGameDialog
        open={!!duplicateGameState}
        onOpenChange={(o) => !o && setDuplicateGameState(null)}
        game={duplicateGameState}
        merchants={merchants}
        onConfirm={performDuplicate}
        loading={duplicateLoading}
      />
    </div>
  );
}

/* ---------- MERCHANTS ---------- */
function MerchantsPanel() {
  const { t } = useTranslation();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const EMPTY_FORM = { name: "", company: "", email: "", password: "", language: "fr", send_welcome: true };
  const [form, setForm] = useState(EMPTY_FORM);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrMerchant, setQrMerchant] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [slugDraft, setSlugDraft] = useState("");
  const [editMerchant, setEditMerchant] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "", company: "", email: "", new_password: "", language: "fr",
    pause_badge: "", pause_title: "", pause_body: "", pause_status: "",
  });

  async function load() {
    const r = await api.get("/merchants");
    setList(r.data.merchants);
  }
  useEffect(() => {
    load();
  }, []);

  async function create() {
    try {
      const r = await api.post("/merchants", form);
      toast.success(t("dashboard.merchant_created"));
      if (form.send_welcome) {
        if (r.data?.welcome_email_sent) toast.success(t("dashboard.welcome_sent", { email: r.data.email }));
        else toast.warning(t("dashboard.welcome_failed"));
      }
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(id) {
    if (!window.confirm(t("dashboard.confirm_delete_merchant"))) return;
    await api.delete(`/merchants/${id}`);
    toast.success(t("dashboard.merchant_deleted"));
    load();
  }

  async function saveSlug(id) {
    try {
      await api.patch(`/merchants/${id}`, { slug: slugDraft });
      toast.success(t("dashboard.merchant_slug_saved"));
      setEditingId(null);
      setSlugDraft("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function saveLogo(id, logo_url) {
    try {
      await api.patch(`/merchants/${id}`, { logo_url });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openEdit(m) {
    setEditMerchant(m);
    setEditForm({
      name: m.name || "",
      company: m.company || "",
      email: m.email || "",
      new_password: "",
      language: m.language || "fr",
      pause_badge: m.pause_badge || "",
      pause_title: m.pause_title || "",
      pause_body: m.pause_body || "",
      pause_status: m.pause_status || "",
      send_welcome: true,
    });
  }

  async function saveEdit() {
    if (!editMerchant) return;
    const payload = {};
    if (editForm.name !== editMerchant.name) payload.name = editForm.name;
    if (editForm.company !== (editMerchant.company || "")) payload.company = editForm.company;
    if (editForm.email !== (editMerchant.email || "")) payload.email = editForm.email;
    if (editForm.new_password) payload.new_password = editForm.new_password;
    if (editForm.language !== (editMerchant.language || "fr")) payload.language = editForm.language;
    ["pause_badge", "pause_title", "pause_body", "pause_status"].forEach((k) => {
      if (editForm[k] !== (editMerchant[k] || "")) payload[k] = editForm[k];
    });
    if (Object.keys(payload).length === 0) {
      setEditMerchant(null);
      return;
    }
    if (!editMerchant.email) payload.send_welcome = editForm.send_welcome;
    try {
      const r = await api.patch(`/merchants/${editMerchant.id}`, payload);
      toast.success(t("dashboard.merchant_updated", "Marchand mis à jour"));
      if (r.data?.welcome_email_sent === true) toast.success(t("dashboard.welcome_sent", { email: payload.email }));
      if (r.data?.welcome_email_sent === false) toast.warning(t("dashboard.welcome_failed"));
      setEditMerchant(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function downloadRgpdPdf(m) {
    try {
      const res = await api.get(`/merchants/${m.id}/rgpd-contract.pdf`, {
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : `contrat-rgpd-${m.id}.pdf`;
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function openQr(m) {
    const url = `${window.location.origin}/m/${m.slug}`;
    setQrMerchant(m);
    setQrUrl(url);
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Button
          onClick={() => setOpen(true)}
          className="rounded-xl h-10 bg-foreground text-background hover:bg-foreground/90"
          data-testid="new-merchant-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {t("dashboard.new_merchant")}
        </Button>
      </div>
      <div className="qrise-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">{t("profile.logo")}</TableHead>
              <TableHead>{t("dashboard.th_name")}</TableHead>
              <TableHead>{t("dashboard.th_company")}</TableHead>
              <TableHead>{t("dashboard.th_email")}</TableHead>
              <TableHead>{t("rgpd.th_column")}</TableHead>
              <TableHead>{t("dashboard.th_permanent_qr")}</TableHead>
              <TableHead className="text-right">{t("dashboard.th_games")}</TableHead>
              <TableHead className="w-32 text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((m) => {
              const permanentUrl = m.slug
                ? `${window.location.origin}/m/${m.slug}`
                : null;
              const isEditing = editingId === m.id;
              return (
                <TableRow key={m.id} data-testid={`merchant-row-${m.id}`}>
                  <TableCell>
                    <MerchantLogoUploader
                      value={m.logo_url}
                      onChange={(url) => saveLogo(m.id, url)}
                      size="sm"
                      testId={`merchant-logo-${m.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell>{m.company}</TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>
                    {m.rgpd_accepted_at ? (
                      <div className="flex items-center gap-1.5">
                        <div
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[10.5px] font-medium"
                          title={`${t("rgpd.accepted_on")} ${formatDate(m.rgpd_accepted_at.slice(0, 10))}`}
                          data-testid={`merchant-rgpd-status-${m.id}`}
                        >
                          <ShieldCheck className="h-3 w-3" />
                          {formatDate(m.rgpd_accepted_at.slice(0, 10))}
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => downloadRgpdPdf(m)}
                          title={t("rgpd.download_pdf")}
                          data-testid={`merchant-rgpd-download-${m.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/30 text-[10.5px] font-medium"
                        data-testid={`merchant-rgpd-status-${m.id}`}
                      >
                        <Clock className="h-3 w-3" />
                        {t("rgpd.pending")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center rounded-lg border border-border bg-muted/40 overflow-hidden">
                          <span className="text-[10px] text-muted-foreground px-2 font-mono">
                            /m/
                          </span>
                          <Input
                            value={slugDraft}
                            onChange={(e) =>
                              setSlugDraft(
                                e.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z0-9-]+/g, "-")
                                  .replace(/^-+|-+$/g, "")
                              )
                            }
                            className="h-7 rounded-none border-0 bg-transparent px-1.5 text-xs font-mono w-40 focus-visible:ring-0"
                            data-testid={`merchant-slug-input-${m.id}`}
                          />
                        </div>
                        <Button
                          size="sm"
                          className="h-7 px-2.5 rounded-lg text-xs bg-foreground text-background hover:bg-foreground/90"
                          onClick={() => saveSlug(m.id)}
                          data-testid={`merchant-slug-save-${m.id}`}
                        >
                          {t("common.save")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 rounded-lg text-xs"
                          onClick={() => {
                            setEditingId(null);
                            setSlugDraft("");
                          }}
                          data-testid={`merchant-slug-cancel-${m.id}`}
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    ) : permanentUrl ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="font-mono text-xs text-muted-foreground truncate max-w-[220px]"
                          title={permanentUrl}
                          data-testid={`merchant-permanent-url-${m.id}`}
                        >
                          /m/{m.slug}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(m.id);
                            setSlugDraft(m.slug);
                          }}
                          className="text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-2"
                          data-testid={`merchant-slug-edit-${m.id}`}
                        >
                          {t("common.edit")}
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">
                        —
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{m.games_count}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {permanentUrl && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-lg text-xs"
                            onClick={() => {
                              navigator.clipboard.writeText(permanentUrl);
                              toast.success(t("dashboard.copied"));
                            }}
                            data-testid={`merchant-copy-link-${m.id}`}
                            title={permanentUrl}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 rounded-lg text-xs"
                            onClick={() => openQr(m)}
                            data-testid={`merchant-qr-${m.id}`}
                          >
                            <QrCode className="h-3 w-3 mr-1" />
                            {t("dashboard.qr")}
                          </Button>
                        </>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => openEdit(m)}
                        data-testid={`merchant-edit-${m.id}`}
                        title={t("common.edit")}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive"
                        onClick={() => remove(m.id)}
                        data-testid={`merchant-delete-${m.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <QRCodeDialog
        open={!!qrUrl}
        onOpenChange={(o) => {
          if (!o) {
            setQrUrl(null);
            setQrMerchant(null);
          }
        }}
        url={qrUrl || ""}
        title={
          qrMerchant
            ? `${t("dashboard.permanent_qr_for")} ${qrMerchant.name}`
            : ""
        }
        fileName={
          qrMerchant?.slug
            ? `qrise-${qrMerchant.slug}`
            : "qrise-merchant"
        }
      />

      {/* Edit merchant dialog */}
      <Dialog
        open={!!editMerchant}
        onOpenChange={(o) => {
          if (!o) setEditMerchant(null);
        }}
      >
        <DialogContent data-testid="edit-merchant-dialog">
          <DialogHeader>
            <DialogTitle>
              {t("dashboard.edit_merchant", "Modifier le marchand")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {editMerchant && !editMerchant.email && (
              <div className="rounded-xl border border-amber-400/50 bg-amber-400/10 px-3 py-2 text-[12px] leading-snug text-amber-800 dark:text-amber-200" data-testid="edit-merchant-no-login-hint">
                {t("dashboard.no_login_hint")}
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {t("dashboard.th_name", "Nom")}
              </label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                className="rounded-xl h-10 mt-1"
                data-testid="edit-merchant-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {t("dashboard.th_company", "Société")}
              </label>
              <Input
                value={editForm.company}
                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                className="rounded-xl h-10 mt-1"
                data-testid="edit-merchant-company-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {t("dashboard.th_email", "Email")}
              </label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="rounded-xl h-10 mt-1"
                data-testid="edit-merchant-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                {t("dashboard.new_password", "Nouveau mot de passe")}{" "}
                <span className="text-[10px] text-muted-foreground/70">
                  ({t("dashboard.optional", "optionnel — laisser vide pour conserver")})
                </span>
              </label>
              <Input
                type="password"
                value={editForm.new_password}
                onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })}
                placeholder="••••••"
                className="rounded-xl h-10 mt-1"
                data-testid="edit-merchant-password-input"
              />
            </div>
            <LanguageSelect
              label={t("dashboard.merchant_language")}
              hint={t("dashboard.merchant_language_hint")}
              value={editForm.language}
              onChange={(v) => setEditForm({ ...editForm, language: v })}
              testId="edit-merchant-language-select"
            />
            {editMerchant && !editMerchant.email && (
              <label className="flex items-start gap-2.5 cursor-pointer text-[13px] leading-snug">
                <Checkbox
                  checked={editForm.send_welcome}
                  onCheckedChange={(v) => setEditForm({ ...editForm, send_welcome: Boolean(v) })}
                  className="mt-0.5"
                  data-testid="edit-merchant-send-welcome-checkbox"
                />
                <span>{t("dashboard.send_welcome")}</span>
              </label>
            )}
            <div className="rounded-xl border border-border/60 p-3 space-y-2" data-testid="edit-merchant-pause-section">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                {t("profile.section_pause")}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug">{t("profile.section_pause_hint")}</p>
              <Input
                value={editForm.pause_badge}
                onChange={(e) => setEditForm({ ...editForm, pause_badge: e.target.value })}
                placeholder={t("profile.pause_badge")}
                className="rounded-xl h-9"
                data-testid="edit-merchant-pause-badge-input"
              />
              <Input
                value={editForm.pause_title}
                onChange={(e) => setEditForm({ ...editForm, pause_title: e.target.value })}
                placeholder={t("profile.pause_title")}
                className="rounded-xl h-9"
                data-testid="edit-merchant-pause-title-input"
              />
              <Textarea
                value={editForm.pause_body}
                onChange={(e) => setEditForm({ ...editForm, pause_body: e.target.value })}
                placeholder={t("profile.pause_body")}
                rows={3}
                className="rounded-xl"
                data-testid="edit-merchant-pause-body-input"
              />
              <Input
                value={editForm.pause_status}
                onChange={(e) => setEditForm({ ...editForm, pause_status: e.target.value })}
                placeholder={t("profile.pause_status")}
                className="rounded-xl h-9"
                data-testid="edit-merchant-pause-status-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMerchant(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={saveEdit}
              data-testid="edit-merchant-save-btn"
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-merchant-dialog">
          <DialogHeader>
            <DialogTitle>{t("dashboard.new_merchant")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder={t("dashboard.merchant_name_ph")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl h-10"
              data-testid="merchant-name-input"
            />
            <Input
              placeholder={t("dashboard.merchant_company_ph")}
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="rounded-xl h-10"
              data-testid="merchant-company-input"
            />
            <Input
              placeholder={t("dashboard.merchant_email_ph")}
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl h-10"
              data-testid="merchant-email-input"
            />
            <Input
              placeholder={t("dashboard.merchant_password_ph")}
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-xl h-10"
              data-testid="merchant-password-input"
            />
            <LanguageSelect
              label={t("dashboard.merchant_language")}
              hint={t("dashboard.merchant_language_hint")}
              value={form.language}
              onChange={(v) => setForm({ ...form, language: v })}
              testId="merchant-language-select"
            />
            <label className="flex items-start gap-2.5 cursor-pointer text-[13px] leading-snug pt-1">
              <Checkbox
                checked={form.send_welcome}
                onCheckedChange={(v) => setForm({ ...form, send_welcome: Boolean(v) })}
                className="mt-0.5"
                data-testid="merchant-send-welcome-checkbox"
              />
              <span>{t("dashboard.send_welcome")}</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={create}
              data-testid="merchant-create-btn"
              className="bg-foreground text-background hover:bg-foreground/90"
            >
              {t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------- PARTICIPANTS ---------- */
export function ParticipantsPanel({ role }) {
  const { t, i18n } = useTranslation();
  const canDelete = role === "admin";
  const [rows, setRows] = useState([]);
  const [games, setGames] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [predictionSession, setPredictionSession] = useState(null);
  const [gameId, setGameId] = useState(() => {
    // Pre-select a game id if the user navigated here from a game card.
    try {
      const pre = sessionStorage.getItem("qrise_preselect_game");
      if (pre) {
        sessionStorage.removeItem("qrise_preselect_game");
        return pre;
      }
    } catch {
      /* ignore */
    }
    return "all";
  });

  async function load() {
    const q = gameId && gameId !== "all" ? `?game_id=${gameId}` : "";
    const r = await api.get(`/sessions${q}`);
    setRows(r.data.sessions);
    setSelected(new Set());
  }

  function toggleSelected(id, on) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function deleteParticipants(ids) {
    if (!ids.length || deleting) return;
    if (!window.confirm(t("dashboard.confirm_delete_participants", { count: ids.length }))) return;
    setDeleting(true);
    try {
      const r = await api.post("/sessions/bulk-delete", { ids });
      toast.success(t("dashboard.participants_deleted", { count: r.data.deleted }));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  }

  async function markRedeemed(sessionId) {
    if (!window.confirm(t("dashboard.confirm_mark_redeemed"))) return;
    try {
      await api.post(`/sessions/${sessionId}/redeem`);
      toast.success(t("dashboard.marked_redeemed"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function resetFingerprint(sessionId) {
    if (!window.confirm(t("dashboard.confirm_reset_fp"))) return;
    try {
      await api.post(`/sessions/${sessionId}/reset-fingerprint`);
      toast.success(t("dashboard.fp_reset_done"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function markShipping(sessionId, status) {
    const confirmKey =
      status === "shipped"
        ? "dashboard.confirm_mark_shipped"
        : "dashboard.confirm_mark_delivered";
    if (!window.confirm(t(confirmKey))) return;
    try {
      await api.post(`/sessions/${sessionId}/shipping-status`, { status });
      toast.success(
        t(
          status === "shipped"
            ? "dashboard.marked_shipped"
            : "dashboard.marked_delivered"
        )
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data.games));
  }, []);
  useEffect(() => {
    load();
  }, [gameId]);

  async function exportData(format) {
    // Auth is carried via Authorization: Bearer header, NOT cookies. We
    // MUST NOT set `credentials: "include"` here — the ingress already
    // adds `Access-Control-Allow-Origin: *` which the browser refuses to
    // pair with `Allow-Credentials: true`, silently dropping the response.
    const lang = i18n.language?.startsWith("en") ? "en" : "fr";
    const q =
      gameId && gameId !== "all"
        ? `?format=${format}&lang=${lang}&game_id=${gameId}`
        : `?format=${format}&lang=${lang}`;
    const token = localStorage.getItem("qrise_token");
    const url = `${API}/sessions/export${q}`;
    const ext = format === "xlsx" ? "xlsx" : format;
    try {
      const r = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          msg = j?.detail || msg;
        } catch (_) {
          // body wasn't JSON — keep the status code as the error message
          void _;
        }
        toast.error(
          t("dashboard.export_failed", "Export impossible") + ` — ${msg}`
        );
        return;
      }
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `participants.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error(
        t("dashboard.export_failed", "Export impossible") +
          ` — ${e?.message || "réseau"}`
      );
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={gameId} onValueChange={setGameId}>
          <SelectTrigger
            className="w-56 rounded-xl h-10"
            data-testid="participants-game-select"
          >
            <SelectValue placeholder={t("dashboard.all_games")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("dashboard.all_games")}</SelectItem>
            {games.map((g) => (
              <SelectItem key={g.id} value={g.id}>
                {g.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              "dashboard.search_participants",
              "Rechercher un participant (nom, email, téléphone, lot)…",
            )}
            className="h-10 rounded-xl pl-9"
            data-testid="participants-search-input"
          />
        </div>
        <div className="flex-1" />
        {canDelete && selected.size > 0 && (
          <Button
            variant="destructive"
            className="rounded-xl h-10"
            disabled={deleting}
            onClick={() => deleteParticipants([...selected])}
            data-testid="delete-selected-participants-btn"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {t("dashboard.delete_selected", { count: selected.size })}
          </Button>
        )}
        <Button
          variant="outline"
          className="rounded-xl h-10"
          onClick={() => exportData("csv")}
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-1.5" />
          {t("dashboard.export_csv")}
        </Button>
        <Button
          className="rounded-xl h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => exportData("xlsx")}
          data-testid="export-xlsx-btn"
        >
          <FileSpreadsheet className="h-4 w-4 mr-1.5" />
          {t("dashboard.export_xlsx")}
        </Button>
      </div>

      <div className="qrise-card overflow-hidden">
        {(() => {
          const q = search.trim().toLowerCase();
          const filtered = !q
            ? rows
            : rows.filter((s) => {
                const p = s.participant || {};
                return [
                  p.first_name,
                  p.last_name,
                  p.email,
                  p.phone,
                  s.game_title,
                  s.result_label,
                  s.coupon_code,
                ]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase()
                  .includes(q);
              });
          const allSelected = filtered.length > 0 && filtered.every((s) => selected.has(s.id));
          const someSelected = !allSelected && filtered.some((s) => selected.has(s.id));
          return (
        <Table>
          <TableHeader>
            <TableRow>
              {canDelete && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(on) =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        filtered.forEach((s) => (on ? next.add(s.id) : next.delete(s.id)));
                        return next;
                      })
                    }
                    aria-label={t("dashboard.select_all_participants")}
                    data-testid="select-all-participants"
                  />
                </TableHead>
              )}
              <TableHead>{t("dashboard.th_date")}</TableHead>
              <TableHead>{t("dashboard.th_game")}</TableHead>
              <TableHead>{t("dashboard.th_name")}</TableHead>
              <TableHead>{t("dashboard.th_email")}</TableHead>
              <TableHead>{t("dashboard.th_phone")}</TableHead>
              {rows.some((s) => s.participant?.address_line1) && (
                <TableHead>{t("dashboard.th_address")}</TableHead>
              )}
              {rows.some((s) => s.shipping_status) && (
                <TableHead className="text-center">
                  {t("dashboard.th_shipping")}
                </TableHead>
              )}
              <TableHead className="text-center">
                {t("dashboard.th_receipt")}
              </TableHead>
              <TableHead className="text-center">
                {t("dashboard.th_optin")}
              </TableHead>
              <TableHead>{t("dashboard.th_result")}</TableHead>
              {rows.some((s) => s.entry_type === "prediction") && (
                <TableHead className="text-center">
                  {t("dashboard.th_predictions", "Pronostics")}
                </TableHead>
              )}
              {rows.some(
                (s) => typeof s.score === "number" && s.score >= 0,
              ) && (
                <TableHead className="text-right">
                  {t("dashboard.th_score", "Score")}
                </TableHead>
              )}
              <TableHead>{t("dashboard.th_win")}</TableHead>
              <TableHead className="text-right">
                {t("dashboard.th_redeemed")}
              </TableHead>
              <TableHead className="text-right">
                {t("dashboard.th_antirejeu")}
              </TableHead>
              {canDelete && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(() => {
              if (filtered.length === 0) {
                return (
                  <TableRow>
                    <TableCell
                      colSpan={16}
                      className="text-center text-sm text-muted-foreground py-10"
                    >
                      {q
                        ? t(
                            "dashboard.no_search_match",
                            "Aucun participant ne correspond à votre recherche.",
                          )
                        : t("dashboard.no_data")}
                    </TableCell>
                  </TableRow>
                );
              }
              return filtered.map((s) => (
              <TableRow
                key={s.id}
                data-testid={`participant-row-${s.id}`}
                data-state={selected.has(s.id) ? "selected" : undefined}
              >
                {canDelete && (
                  <TableCell className="w-10">
                    <Checkbox
                      checked={selected.has(s.id)}
                      onCheckedChange={(on) => toggleSelected(s.id, !!on)}
                      aria-label={t("dashboard.select_participant")}
                      data-testid={`select-participant-${s.id}`}
                    />
                  </TableCell>
                )}
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(s.created_at).toLocaleString()}
                </TableCell>
                <TableCell>{s.game_title}</TableCell>
                <TableCell className="font-medium">
                  {s.participant?.first_name} {s.participant?.last_name}
                </TableCell>
                <TableCell
                  className={
                    s.participant?._masked
                      ? "font-mono text-xs text-muted-foreground"
                      : ""
                  }
                  data-testid={`participant-email-${s.id}`}
                >
                  {s.participant?.email}
                </TableCell>
                <TableCell
                  className={
                    s.participant?._masked
                      ? "font-mono text-xs text-muted-foreground"
                      : ""
                  }
                  data-testid={`participant-phone-${s.id}`}
                >
                  {s.participant?.phone}
                </TableCell>
                {rows.some((r) => r.participant?.address_line1) && (
                  <TableCell
                    className="text-xs leading-snug max-w-[220px]"
                    data-testid={`participant-address-${s.id}`}
                  >
                    {s.participant?.address_line1 ? (
                      <div>
                        <div className="font-medium text-foreground/90 truncate">
                          {s.participant.address_line1}
                        </div>
                        {s.participant.address_line2 && (
                          <div className="text-muted-foreground truncate">
                            {s.participant.address_line2}
                          </div>
                        )}
                        <div className="text-muted-foreground">
                          {s.participant.postal_code} {s.participant.city}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}
                {rows.some((r) => r.shipping_status) && (
                  <TableCell
                    className="text-center"
                    data-testid={`participant-shipping-${s.id}`}
                  >
                    <ShippingStatusCell
                      session={s}
                      onMark={markShipping}
                    />
                  </TableCell>
                )}
                <TableCell
                  className="text-center"
                  data-testid={`participant-receipt-${s.id}`}
                >
                  <ReceiptCell
                    receiptUrl={s.receipt_url}
                    ocr={s.receipt_ocr}
                  />
                </TableCell>
                <TableCell
                  className="text-center"
                  data-testid={`participant-optin-${s.id}`}
                >
                  <span
                    className={`inline-flex items-center justify-center h-5 w-5 rounded border ${
                      s.participant?.marketing_opt_in
                        ? "bg-emerald-500 border-emerald-500 text-white"
                        : "bg-transparent border-border"
                    }`}
                    aria-label={
                      s.participant?.marketing_opt_in
                        ? t("dashboard.opt_in_yes")
                        : t("dashboard.opt_in_no")
                    }
                    title={
                      s.participant?.marketing_opt_in
                        ? t("dashboard.opt_in_yes")
                        : t("dashboard.opt_in_no")
                    }
                  >
                    {s.participant?.marketing_opt_in && (
                      <CheckCircle2 className="h-3 w-3" strokeWidth={3} />
                    )}
                  </span>
                </TableCell>
                <TableCell>{s.result_label}</TableCell>
                {rows.some((r) => r.entry_type === "prediction") && (
                  <TableCell
                    className="text-center"
                    data-testid={`participant-prediction-${s.id}`}
                  >
                    {s.entry_type === "prediction" &&
                    (s.predictions?.length || 0) > 0 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-lg text-[11px] font-semibold"
                        onClick={() => setPredictionSession(s)}
                        data-testid={`view-predictions-${s.id}`}
                      >
                        <Trophy className="h-3 w-3 mr-1" />
                        {t("dashboard.view_predictions", "Voir pronostics")}
                      </Button>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                )}
                {rows.some(
                  (r) => typeof r.score === "number" && r.score >= 0,
                ) && (
                  <TableCell
                    className="text-right tabular-nums"
                    data-testid={`participant-score-${s.id}`}
                  >
                    {typeof s.score === "number" ? (
                      <div className="leading-tight">
                        <div className="font-semibold">
                          {s.score} {t("dashboard.score_unit", "pts")}
                        </div>
                        {typeof s.time_taken_ms === "number" &&
                          s.time_taken_ms > 0 && (
                            <div className="text-[10.5px] text-muted-foreground">
                              {(s.time_taken_ms / 1000).toFixed(1)}s
                            </div>
                          )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {s.is_win ? (
                    <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-0 rounded-full">
                      {t("dashboard.badge_win")}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-full">
                      {t("dashboard.badge_lose")}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {s.is_win ? (
                    s.redeemed ? (
                      <Badge
                        className="bg-foreground/10 text-foreground border-0 rounded-full inline-flex items-center gap-1"
                        data-testid={`redeemed-badge-${s.id}`}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {t("dashboard.badge_redeemed")}
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 rounded-lg text-[11px] font-semibold"
                        onClick={() => markRedeemed(s.id)}
                        data-testid={`mark-redeemed-${s.id}`}
                      >
                        {t("dashboard.mark_redeemed")}
                      </Button>
                    )
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {s.fingerprint_reset ? (
                    <Badge
                      variant="outline"
                      className="rounded-full text-[10.5px] font-medium"
                      data-testid={`fp-reset-badge-${s.id}`}
                    >
                      {t("dashboard.fp_reset_done_badge")}
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 rounded-lg text-[11px] text-muted-foreground hover:text-foreground"
                      onClick={() => resetFingerprint(s.id)}
                      title={t("dashboard.reset_fp_hint")}
                      data-testid={`reset-fp-${s.id}`}
                    >
                      <RefreshCcw className="h-3 w-3 mr-1" />
                      {t("dashboard.reset_fp")}
                    </Button>
                  )}
                </TableCell>
                {canDelete && (
                  <TableCell className="text-right w-10">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 rounded-lg text-muted-foreground hover:text-destructive"
                      onClick={() => deleteParticipants([s.id])}
                      title={t("dashboard.delete_participant")}
                      data-testid={`delete-participant-${s.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ));
            })()}
          </TableBody>
        </Table>
          );
        })()}
      </div>

      <PredictionsDetailDialog
        open={!!predictionSession}
        onOpenChange={(open) => {
          if (!open) setPredictionSession(null);
        }}
        session={predictionSession}
      />
    </div>
  );
}

/* ---------- ANALYTICS ---------- */
function AnalyticsPanel() {
  return <AnalyticsDashboard />;
}


/* ---------- SHIPPING STATUS CELL ---------- */
function ShippingStatusCell({ session, onMark }) {
  const { t } = useTranslation();
  const status = session.shipping_status || "";
  if (!status) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (status === "delivered") {
    return (
      <Badge
        className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 rounded-full inline-flex items-center gap-1"
        data-testid={`shipping-delivered-${session.id}`}
      >
        <PackageCheck className="h-3 w-3" />
        {t("dashboard.shipping_delivered")}
      </Badge>
    );
  }
  if (status === "shipped") {
    return (
      <div className="inline-flex flex-col items-center gap-1">
        <Badge
          className="bg-sky-500/15 text-sky-700 dark:text-sky-400 border-0 rounded-full inline-flex items-center gap-1"
          data-testid={`shipping-shipped-${session.id}`}
        >
          <Truck className="h-3 w-3" />
          {t("dashboard.shipping_shipped")}
        </Badge>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 rounded-md text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => onMark(session.id, "delivered")}
          data-testid={`mark-delivered-${session.id}`}
        >
          {t("dashboard.mark_delivered")}
        </Button>
      </div>
    );
  }
  // to_ship
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <Badge
        variant="outline"
        className="rounded-full text-[10.5px] font-medium border-amber-500/40 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1"
        data-testid={`shipping-to-ship-${session.id}`}
      >
        <MapPin className="h-3 w-3" />
        {t("dashboard.shipping_to_ship")}
      </Badge>
      <Button
        size="sm"
        variant="outline"
        className="h-6 rounded-md text-[10px] font-semibold"
        onClick={() => onMark(session.id, "shipped")}
        data-testid={`mark-shipped-${session.id}`}
      >
        <Truck className="h-3 w-3 mr-1" />
        {t("dashboard.mark_shipped")}
      </Button>
    </div>
  );
}
